"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiAttachment } from "@/generated/models/ApiAttachment";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import type { WsAttachmentStatusUpdateMessage, WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import type { Drop, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useMarkWaveNotificationsRead } from "@/hooks/useMarkWaveNotificationsRead";
import { fetchDropByIdBatched } from "@/services/api/drop-api";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useWaveEligibility } from "../WaveEligibilityContext";
import type { WaveDataStoreUpdater } from "./types";
import { WebSocketStatus } from "@/services/websocket/WebSocketTypes";
import { recordReactionRealtimeReconciliation } from "@/utils/monitoring/dropReactionMonitoring";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { reconcileDropAuthenticatedPollVote } from "@/helpers/waves/poll-vote-reconciliation";
import { updateAttachmentInCachedDrops, updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
interface UseWaveRealtimeUpdaterProps extends WaveDataStoreUpdater {
  readonly activeWaveId: string | null;
  readonly registerWave: (waveId: string) => void;
  readonly syncNewestMessages: (waveId: string, sinceSerialNo: number, signal: AbortSignal) => Promise<{
    drops: ApiDrop[] | null;
    highestSerialNo: number | null;
  }>;
  readonly removeWaveDeliveredNotifications: (waveId: string) => Promise<void>;
  readonly isWaveMuted: (waveId: string) => boolean;
}
export enum ProcessIncomingDropType {
  DROP_RATING_UPDATE = "DROP_RATING_UPDATE",
  DROP_INSERT = "DROP_INSERT",
  DROP_REACTION_UPDATE = "DROP_REACTION_UPDATE",
}
type ProcessIncomingDropFn = (dropData: ApiDrop, type: ProcessIncomingDropType, options?: ProcessIncomingDropOptions) => Promise<void>;
export interface ProcessIncomingDropOptions {
  readonly preferExistingPollVote?: boolean;
}
function replaceAttachmentInPart(part: ApiDropPart, attachment: ApiAttachment): ApiDropPart {
  const attachments = part.attachments;
  const hasAttachment = attachments.some(item => item.attachment_id === attachment.attachment_id);
  if (!hasAttachment) {
    return part;
  }
  return {
    ...part,
    attachments: attachments.map(item => item.attachment_id === attachment.attachment_id ? attachment : item)
  };
}
const getIncomingWaveId = (drop: ApiDrop): string | null => {
  const wave = (drop as {
    readonly wave?: {
      readonly id?: unknown;
    };
  }).wave;
  return typeof wave?.id === "string" && wave.id.length > 0 ? wave.id : null;
};
const isCanonicalDropUpdate = (type: ProcessIncomingDropType): boolean => type === ProcessIncomingDropType.DROP_RATING_UPDATE || type === ProcessIncomingDropType.DROP_REACTION_UPDATE;
const reportBackgroundTaskError = (message: string, error: unknown): void => {
  console.error(message, error);
};
const shouldApplyCanonicalDrop = (type: ProcessIncomingDropType, drop: ApiDrop): boolean => {
  if (type !== ProcessIncomingDropType.DROP_REACTION_UPDATE) {
    return true;
  }
  return recordReactionRealtimeReconciliation({
    drop: {
      id: drop.id,
      wave: {
        id: drop.wave.id
      },
      context_profile_context: drop.context_profile_context
    },
    websocketStatus: WebSocketStatus.CONNECTED
  }).shouldApplyCanonicalDrop;
};
interface CanonicalDropUpdateParams {
  readonly dropId: string;
  readonly existingDrop: ExtendedDrop;
  readonly waveId: string;
  readonly type: ProcessIncomingDropType;
  readonly options: ProcessIncomingDropOptions;
  readonly queryClient: QueryClient;
  readonly updateData: WaveDataStoreUpdater["updateData"];
}
const applyCanonicalDropUpdate = async ({
  dropId,
  existingDrop,
  waveId,
  type,
  options,
  queryClient,
  updateData
}: CanonicalDropUpdateParams): Promise<void> => {
  const apiDrop = await fetchDropByIdBatched(dropId);
  const preferExistingPollVote = options.preferExistingPollVote;
  const reconciledApiDrop = preferExistingPollVote === undefined ? reconcileDropAuthenticatedPollVote(apiDrop, existingDrop) : reconcileDropAuthenticatedPollVote(apiDrop, existingDrop, {
    preferExistingVote: preferExistingPollVote
  });
  if (!shouldApplyCanonicalDrop(type, reconciledApiDrop)) {
    return;
  }
  if (type === ProcessIncomingDropType.DROP_REACTION_UPDATE) {
    updateDropInCachedDrops(queryClient, reconciledApiDrop);
  }
  updateData({
    key: waveId,
    drops: [{
      ...reconciledApiDrop,
      type: DropSize.FULL,
      stableHash: existingDrop.stableHash,
      stableKey: existingDrop.stableKey
    }]
  });
};
const buildOptimisticDrop = ({
  drop,
  existingDrop,
  options
}: {
  readonly drop: ApiDrop;
  readonly existingDrop: ExtendedDrop | null;
  readonly options: ProcessIncomingDropOptions;
}): ExtendedDrop => {
  const preferExistingPollVote = options.preferExistingPollVote;
  let reconciledDrop = drop;
  if (existingDrop !== null && preferExistingPollVote === undefined) {
    reconciledDrop = reconcileDropAuthenticatedPollVote(drop, existingDrop);
  }
  if (existingDrop !== null && preferExistingPollVote !== undefined) {
    reconciledDrop = reconcileDropAuthenticatedPollVote(drop, existingDrop, {
      preferExistingVote: preferExistingPollVote
    });
  }
  return {
    ...reconciledDrop,
    type: DropSize.FULL,
    author: {
      ...reconciledDrop.author,
      subscribed_actions: existingDrop === null ? reconciledDrop.author.subscribed_actions : existingDrop.author.subscribed_actions
    },
    wave: {
      ...reconciledDrop.wave,
      authenticated_user_eligible_to_participate: existingDrop === null ? reconciledDrop.wave.authenticated_user_eligible_to_participate : existingDrop.wave.authenticated_user_eligible_to_participate,
      authenticated_user_eligible_to_vote: existingDrop === null ? reconciledDrop.wave.authenticated_user_eligible_to_vote : existingDrop.wave.authenticated_user_eligible_to_vote,
      authenticated_user_eligible_to_chat: existingDrop === null ? reconciledDrop.wave.authenticated_user_eligible_to_chat : existingDrop.wave.authenticated_user_eligible_to_chat,
      authenticated_user_admin: existingDrop === null ? reconciledDrop.wave.authenticated_user_admin : existingDrop.wave.authenticated_user_admin
    },
    stableKey: reconciledDrop.id,
    stableHash: reconciledDrop.id,
    context_profile_context: existingDrop === null ? reconciledDrop.context_profile_context ?? null : existingDrop.context_profile_context
  };
};
function replaceAttachmentInDrop(drop: Drop, attachment: ApiAttachment): Drop {
  if (drop.type !== DropSize.FULL) {
    return drop;
  }
  const parts = drop.parts.map(part => replaceAttachmentInPart(part, attachment));
  const changed = parts.some((part, index) => part !== drop.parts[index]);
  return changed ? {
    ...drop,
    parts
  } : drop;
}
function replaceAttachmentInDrops(drops: Drop[], attachment: ApiAttachment): {
  drops: Drop[];
  changed: boolean;
} {
  const updatedDrops = drops.map(drop => replaceAttachmentInDrop(drop, attachment));
  const changed = updatedDrops.some((drop, index) => drop !== drops[index]);
  return {
    drops: updatedDrops,
    changed
  };
}
type InitiateFetchNewestCycleFn = (waveId: string, sinceSerialNo: number) => Promise<void>;
const useNewestMessagesSync = ({
  getData,
  updateData,
  syncNewestMessages
}: Pick<UseWaveRealtimeUpdaterProps, "getData" | "updateData" | "syncNewestMessages">): InitiateFetchNewestCycleFn => {
  const isFetchingNewestRef = useRef<Record<string, boolean>>({});
  const needsRefetchAfterCurrentRef = useRef<Record<string, boolean>>({});
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const cleanupController = useCallback((waveId: string) => {
    if (abortControllersRef.current[waveId]) {
      delete abortControllersRef.current[waveId];
    }
  }, []);
  const initiateFetchNewestCycle = useCallback(async (waveId_0: string, sinceSerialNo: number) => {
    if (isFetchingNewestRef.current[waveId_0]) {
      needsRefetchAfterCurrentRef.current[waveId_0] = true;
      return;
    }
    isFetchingNewestRef.current[waveId_0] = true;
    const controller = new AbortController();
    abortControllersRef.current[waveId_0] = controller;
    try {
      const {
        drops: fetchedDrops,
        highestSerialNo: fetchedHighestSerial
      } = await syncNewestMessages(waveId_0, sinceSerialNo, controller.signal);
      if (fetchedDrops) {
        const currentData = getData(waveId_0);
        if (currentData) {
          const newDrops: ExtendedDrop[] = fetchedDrops.map(drop => ({
            ...drop,
            type: DropSize.FULL,
            stableKey: drop.id,
            stableHash: drop.id
          }));
          updateData({
            key: waveId_0,
            drops: newDrops,
            latestFetchedSerialNo: fetchedHighestSerial ?? null
          });
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      console.error("Error fetching newest messages:", error);
    } finally {
      cleanupController(waveId_0);
      isFetchingNewestRef.current[waveId_0] = false;
      if (needsRefetchAfterCurrentRef.current[waveId_0]) {
        needsRefetchAfterCurrentRef.current[waveId_0] = false;
        const latestFetchedSerialNo = getData(waveId_0)?.latestFetchedSerialNo ?? null;
        if (latestFetchedSerialNo !== null) {
          await initiateFetchNewestCycle(waveId_0, latestFetchedSerialNo);
        }
      }
    }
  }, [getData, updateData, syncNewestMessages, cleanupController]);
  useEffect(() => {
    return () => {
      Object.values(abortControllersRef.current).forEach(controller_0 => controller_0.abort());
      abortControllersRef.current = {};
      isFetchingNewestRef.current = {};
      needsRefetchAfterCurrentRef.current = {};
    };
  }, []);
  return initiateFetchNewestCycle;
};
const useActiveWaveReadMarker = t0 => {
  const $ = _c(12);
  const {
    activeWaveId,
    removeWaveDeliveredNotifications
  } = t0;
  const activeWaveIdRef = useRef(activeWaveId);
  const pendingDeliveredNotificationsRef = useRef(null);
  const pendingReadNotificationsRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== activeWaveId) {
    t1 = () => {
      activeWaveIdRef.current = activeWaveId;
    };
    t2 = [activeWaveId];
    $[0] = activeWaveId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  const markWaveNotificationsRead = useMarkWaveNotificationsRead();
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = waveId => activeWaveIdRef.current === waveId && document.visibilityState === "visible";
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const canSendReadForWave = t3;
  let t4;
  if ($[4] !== removeWaveDeliveredNotifications) {
    t4 = async waveId_0 => {
      ;
      try {
        await removeWaveDeliveredNotifications(waveId_0);
      } catch (t5) {
        const error = t5;
        reportBackgroundTaskError("Failed to remove wave delivered notifications:", error);
      }
    };
    $[4] = removeWaveDeliveredNotifications;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const removeDeliveredNotifications = t4;
  let t5;
  if ($[6] !== markWaveNotificationsRead) {
    t5 = async waveId_1 => {
      ;
      try {
        await markWaveNotificationsRead(waveId_1, {
          shouldSend: () => canSendReadForWave(waveId_1)
        });
      } catch (t6) {
        const error_0 = t6;
        reportBackgroundTaskError("Failed to mark wave as read:", error_0);
      }
    };
    $[6] = markWaveNotificationsRead;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const markNotificationsRead = t5;
  let t6;
  if ($[8] !== activeWaveId || $[9] !== markNotificationsRead || $[10] !== removeDeliveredNotifications) {
    t6 = waveId_2 => {
      if (activeWaveId !== waveId_2 || document.visibilityState !== "visible") {
        return;
      }
      pendingDeliveredNotificationsRef.current = removeDeliveredNotifications(waveId_2);
      pendingReadNotificationsRef.current = markNotificationsRead(waveId_2);
    };
    $[8] = activeWaveId;
    $[9] = markNotificationsRead;
    $[10] = removeDeliveredNotifications;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
const useVisibilityEligibilityRefresh = () => {
  const $ = _c(4);
  const {
    refreshEligibility
  } = useWaveEligibility();
  const tabJustBecameVisibleRef = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          tabJustBecameVisibleRef.current = true;
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== refreshEligibility) {
    t2 = async waveId => {
      if (!tabJustBecameVisibleRef.current) {
        return;
      }
      tabJustBecameVisibleRef.current = false;
      ;
      try {
        await refreshEligibility(waveId);
      } catch (t3) {
        const error = t3;
        reportBackgroundTaskError("Failed to refresh wave eligibility:", error);
      }
    };
    $[2] = refreshEligibility;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
interface ApplyCanonicalDropUpdateForExistingDropParams {
  readonly dropId: string;
  readonly existingDrop: ExtendedDrop;
  readonly waveId: string;
  readonly type: ProcessIncomingDropType;
  readonly options: ProcessIncomingDropOptions;
}
const useCanonicalDropUpdateForExistingDrop = t0 => {
  const $ = _c(3);
  const {
    queryClient,
    updateData
  } = t0;
  let t1;
  if ($[0] !== queryClient || $[1] !== updateData) {
    t1 = async t2 => {
      const {
        dropId,
        existingDrop,
        waveId,
        type,
        options
      } = t2;
      ;
      try {
        await applyCanonicalDropUpdate({
          dropId,
          existingDrop,
          waveId,
          type,
          options,
          queryClient,
          updateData
        });
      } catch (t3) {
        const error = t3;
        reportBackgroundTaskError("Failed to apply canonical drop update:", error);
      }
    };
    $[0] = queryClient;
    $[1] = updateData;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const useNewestMessagesAfterDropUpdate = initiateFetchNewestCycle => {
  const $ = _c(2);
  let t0;
  if ($[0] !== initiateFetchNewestCycle) {
    t0 = async (waveId, serialNoForFetch, optimisticDropId) => {
      if (serialNoForFetch === null || optimisticDropId.startsWith("temp-")) {
        return;
      }
      ;
      try {
        await initiateFetchNewestCycle(waveId, serialNoForFetch);
      } catch (t1) {
        const error = t1;
        reportBackgroundTaskError("Failed to sync newest wave messages:", error);
      }
    };
    $[0] = initiateFetchNewestCycle;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
interface UseProcessIncomingDropParams extends Pick<UseWaveRealtimeUpdaterProps, "activeWaveId" | "getData" | "updateData" | "registerWave" | "syncNewestMessages" | "removeWaveDeliveredNotifications" | "isWaveMuted"> {
  readonly queryClient: QueryClient;
}
const useProcessIncomingDrop = t0 => {
  const $ = _c(20);
  const {
    activeWaveId,
    getData,
    updateData,
    registerWave,
    syncNewestMessages,
    removeWaveDeliveredNotifications,
    isWaveMuted,
    queryClient
  } = t0;
  let t1;
  if ($[0] !== getData || $[1] !== syncNewestMessages || $[2] !== updateData) {
    t1 = {
      getData,
      updateData,
      syncNewestMessages
    };
    $[0] = getData;
    $[1] = syncNewestMessages;
    $[2] = updateData;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const initiateFetchNewestCycle = useNewestMessagesSync(t1);
  let t2;
  if ($[4] !== activeWaveId || $[5] !== removeWaveDeliveredNotifications) {
    t2 = {
      activeWaveId,
      removeWaveDeliveredNotifications
    };
    $[4] = activeWaveId;
    $[5] = removeWaveDeliveredNotifications;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const markActiveWaveAsRead = useActiveWaveReadMarker(t2);
  const refreshEligibilityAfterVisibilityChange = useVisibilityEligibilityRefresh();
  let t3;
  if ($[7] !== queryClient || $[8] !== updateData) {
    t3 = {
      queryClient,
      updateData
    };
    $[7] = queryClient;
    $[8] = updateData;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const applyCanonicalDropUpdateForExistingDrop = useCanonicalDropUpdateForExistingDrop(t3);
  const syncNewestMessagesAfterDropUpdate = useNewestMessagesAfterDropUpdate(initiateFetchNewestCycle);
  let t4;
  if ($[10] !== applyCanonicalDropUpdateForExistingDrop || $[11] !== getData || $[12] !== isWaveMuted || $[13] !== markActiveWaveAsRead || $[14] !== queryClient || $[15] !== refreshEligibilityAfterVisibilityChange || $[16] !== registerWave || $[17] !== syncNewestMessagesAfterDropUpdate || $[18] !== updateData) {
    t4 = async (drop, type, t5) => {
      const options = t5 === undefined ? {} : t5;
      const waveId = getIncomingWaveId(drop);
      if (waveId === null) {
        return;
      }
      if (type !== ProcessIncomingDropType.DROP_REACTION_UPDATE) {
        const preferExistingPollVote = options.preferExistingPollVote;
        if (preferExistingPollVote === undefined) {
          updateDropInCachedDrops(queryClient, drop);
        } else {
          updateDropInCachedDrops(queryClient, drop, {
            preferExistingPollVote
          });
        }
      }
      if (isWaveMuted(waveId)) {
        return;
      }
      await refreshEligibilityAfterVisibilityChange(waveId);
      const currentData = getData(waveId);
      if (!currentData) {
        registerWave(waveId);
        return;
      }
      const existingDrop = currentData.drops.find(d => d.id === drop.id);
      if (existingDrop?.type === DropSize.LIGHT) {
        return;
      }
      const existingFullDrop = existingDrop ?? null;
      if (isCanonicalDropUpdate(type)) {
        if (existingFullDrop === null) {
          return;
        }
        await applyCanonicalDropUpdateForExistingDrop({
          dropId: drop.id,
          existingDrop: existingFullDrop,
          waveId,
          type,
          options
        });
        return;
      }
      const optimisticDrop = buildOptimisticDrop({
        drop,
        existingDrop: existingFullDrop,
        options
      });
      updateData({
        key: waveId,
        drops: [optimisticDrop]
      });
      await syncNewestMessagesAfterDropUpdate(waveId, currentData.latestFetchedSerialNo, optimisticDrop.id);
      markActiveWaveAsRead(waveId);
    };
    $[10] = applyCanonicalDropUpdateForExistingDrop;
    $[11] = getData;
    $[12] = isWaveMuted;
    $[13] = markActiveWaveAsRead;
    $[14] = queryClient;
    $[15] = refreshEligibilityAfterVisibilityChange;
    $[16] = registerWave;
    $[17] = syncNewestMessagesAfterDropUpdate;
    $[18] = updateData;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};
const useAttachmentStatusUpdate = t0 => {
  const $ = _c(5);
  const {
    activeWaveId,
    getData,
    updateData,
    queryClient
  } = t0;
  let t1;
  if ($[0] !== activeWaveId || $[1] !== getData || $[2] !== queryClient || $[3] !== updateData) {
    t1 = attachment => {
      updateAttachmentInCachedDrops(queryClient, attachment);
      if (!activeWaveId) {
        return;
      }
      const currentData = getData(activeWaveId);
      if (!currentData) {
        return;
      }
      const {
        drops,
        changed
      } = replaceAttachmentInDrops(currentData.drops, attachment);
      if (changed) {
        updateData({
          key: activeWaveId,
          drops
        });
      }
    };
    $[0] = activeWaveId;
    $[1] = getData;
    $[2] = queryClient;
    $[3] = updateData;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const useDropUpdateMessages = processIncomingDrop => {
  const $ = _c(6);
  const pendingDropUpdateRef = useRef(null);
  let t0;
  if ($[0] !== processIncomingDrop) {
    t0 = messageData => {
      pendingDropUpdateRef.current = processIncomingDrop(messageData, ProcessIncomingDropType.DROP_INSERT, {
        preferExistingPollVote: true
      });
    };
    $[0] = processIncomingDrop;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t0);
  let t1;
  if ($[2] !== processIncomingDrop) {
    t1 = messageData_0 => {
      pendingDropUpdateRef.current = processIncomingDrop(messageData_0, ProcessIncomingDropType.DROP_RATING_UPDATE, {
        preferExistingPollVote: true
      });
    };
    $[2] = processIncomingDrop;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useWebSocketMessage(WsMessageType.DROP_RATING_UPDATE, t1);
  let t2;
  if ($[4] !== processIncomingDrop) {
    t2 = messageData_1 => {
      pendingDropUpdateRef.current = processIncomingDrop(messageData_1, ProcessIncomingDropType.DROP_REACTION_UPDATE, {
        preferExistingPollVote: true
      });
    };
    $[4] = processIncomingDrop;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useWebSocketMessage(WsMessageType.DROP_REACTION_UPDATE, t2);
};
export function useWaveRealtimeUpdater(t0) {
  const $ = _c(19);
  const {
    activeWaveId,
    getData,
    updateData,
    registerWave,
    syncNewestMessages,
    removeDrop,
    removeWaveDeliveredNotifications,
    isWaveMuted
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== activeWaveId || $[1] !== getData || $[2] !== isWaveMuted || $[3] !== queryClient || $[4] !== registerWave || $[5] !== removeWaveDeliveredNotifications || $[6] !== syncNewestMessages || $[7] !== updateData) {
    t1 = {
      activeWaveId,
      getData,
      updateData,
      registerWave,
      syncNewestMessages,
      removeWaveDeliveredNotifications,
      isWaveMuted,
      queryClient
    };
    $[0] = activeWaveId;
    $[1] = getData;
    $[2] = isWaveMuted;
    $[3] = queryClient;
    $[4] = registerWave;
    $[5] = removeWaveDeliveredNotifications;
    $[6] = syncNewestMessages;
    $[7] = updateData;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const processIncomingDrop = useProcessIncomingDrop(t1);
  let t2;
  if ($[9] !== removeDrop) {
    t2 = (waveId, dropId) => {
      removeDrop(waveId, dropId);
    };
    $[9] = removeDrop;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const processDropRemoved = t2;
  let t3;
  if ($[11] !== activeWaveId || $[12] !== getData || $[13] !== queryClient || $[14] !== updateData) {
    t3 = {
      activeWaveId,
      getData,
      updateData,
      queryClient
    };
    $[11] = activeWaveId;
    $[12] = getData;
    $[13] = queryClient;
    $[14] = updateData;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const processAttachmentStatusUpdate = useAttachmentStatusUpdate(t3);
  useDropUpdateMessages(processIncomingDrop);
  useWebSocketMessage(WsMessageType.ATTACHMENT_STATUS_UPDATE, processAttachmentStatusUpdate);
  let t4;
  if ($[16] !== processDropRemoved || $[17] !== processIncomingDrop) {
    t4 = {
      processIncomingDrop,
      processDropRemoved
    };
    $[16] = processDropRemoved;
    $[17] = processIncomingDrop;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}